import BottomSheet from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheet/BottomSheet'
import {
  AddFileParams,
  AddFolderParams,
  File,
  Id,
  Resource,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import { confirmationAlert, errorAlert } from 'shared/alerts'
import { useShareResourceContext } from './chat_group_folders/send_file_folders/context'
import { useAddFile } from './queries/add_file'
import { useAddFolder } from './queries/add_folder'
import { useCopyFile, useCopyFolder } from './queries/copy'
import { useDeleteFile, useDeleteFolder } from './queries/delete'
import { useMoveFile, useMoveFolder } from './queries/move'
import { FileFolderSortOption, FileFolderViewMode } from './types'
import { isImageType, isVideoType } from './util'
import {
  FileFolderBottomSheet,
  FileFolderModalType,
} from './views/bottom_sheet'

interface FileFolderActionsContextValue {
  onLongPressResource: (resource: Resource) => void
  onPressFile: (file: File) => void
  setConversationId: (conversationId: string) => void
  onPerformSelectedAction(currentFolderId: Id | undefined): Promise<void>
  selectedAction: string | undefined
  onCancelSelectedAction(): void
  setShouldNavigateToFoldersScreen: (shouldNavigate: boolean) => void
  openSortOrderBottomSheet: () => void
  sortOption: FileFolderSortOption
  viewMode: FileFolderViewMode
  onAddFile: (fields: AddFileParams) => Promise<void>
  onAddFolder: (fields: AddFolderParams) => Promise<void>
}

interface Props {
  children: React.ReactNode
}

const FileFolderActionsContext = createContext(
  {} as FileFolderActionsContextValue
)

export function FileFolderActionsProvider(props: Props) {
  const { handleNavigation } = useRoutingContext()
  const { setSelectedConversationIds, setSelectedRecipientIds } =
    useShareResourceContext()

  const [longPressedResource, setLongPressedResource] = useState<
    Resource | undefined
  >()
  const [bottomSheetType, setBottomSheetType] =
    useState<FileFolderModalType>('options')
  const [shouldNavigateToFoldersScreen, setShouldNavigateToFoldersScreen] =
    useState<boolean>(true)
  const [sortOption, setSortOption] = useState<FileFolderSortOption>('dateDesc')
  const [viewMode, setViewMode] = useState<FileFolderViewMode>('list')
  const [conversationId, setConversationId] = useState<string>()
  const [selectedResource, setSelectedResource] = useState<
    Resource | undefined
  >()
  const [selectedAction, setSelectedAction] = useState<string | undefined>()
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false)
  const fileFolderBottomSheetRef = useRef<BottomSheet>(null)

  const addFolderMutation = useAddFolder()
  const addFileMutation = useAddFile()
  const deleteFileMutation = useDeleteFile()
  const deleteFolderMutation = useDeleteFolder()
  const copyFileMutation = useCopyFile()
  const copyFolderMutation = useCopyFolder()
  const moveFileMutation = useMoveFile()
  const moveFolderMutation = useMoveFolder()

  const onCancelSelectedAction = useCallback(() => {
    setSelectedResource(undefined)
    setSelectedAction(undefined)
  }, [])

  const onPerformSelectedAction = useCallback(
    async (currentFolderId: Id | undefined) => {
      if (!selectedResource || !selectedAction) {
        return
      }
      if (selectedResource.chatGroupId !== conversationId) {
        errorAlert(
          new Error(t('fileFolderActionCrossGroupErrorDescription')),
          t('fileFolderActionErrorTitle')
        )
      } else {
        try {
          switch (selectedAction) {
            case 'move':
              if (selectedResource.type === 'file') {
                await moveFileMutation.mutateAsync({
                  fileId: selectedResource.id,
                  newParentFolderId: currentFolderId,
                  newChatGroupId: conversationId,
                })
              } else if (selectedResource.type === 'folder') {
                await moveFolderMutation.mutateAsync({
                  folderId: selectedResource.id,
                  newParentFolderId: currentFolderId,
                  newChatGroupId: conversationId,
                })
              }
              break
            case 'copy':
              if (selectedResource.type === 'file') {
                await copyFileMutation.mutateAsync({
                  fileId: selectedResource.id,
                  newParentFolderId: currentFolderId,
                  newChatGroupId: conversationId,
                })
              } else if (selectedResource.type === 'folder') {
                await copyFolderMutation.mutateAsync({
                  folderId: selectedResource.id,
                  newParentFolderId: currentFolderId,
                  newChatGroupId: conversationId,
                })
              }
          }
          setSelectedResource(undefined)
          setSelectedAction(undefined)
        } catch (e) {
          errorAlert(e, t('fileFolderActionErrorTitle'))
        }
      }
    },
    [
      selectedResource,
      selectedAction,
      conversationId,
      moveFileMutation,
      moveFolderMutation,
      copyFileMutation,
      copyFolderMutation,
    ]
  )

  const onDeleteResource = useCallback(
    async (resource: Resource | undefined) => {
      if (!resource) {
        return
      }
      if (resource.type === 'file') {
        await deleteFileMutation.mutateAsync({ fileId: resource.id })
      } else if (resource.type === 'folder') {
        await deleteFolderMutation.mutateAsync({ folderId: resource.id })
      }
    },
    [deleteFileMutation, deleteFolderMutation]
  )

  const onShareResource = useCallback(
    (resource: Resource, requestAcknowledgement: boolean) => {
      setSelectedRecipientIds([])
      setSelectedConversationIds([])
      handleNavigation({
        routeName: 'share_resource_recipients',
        routeParams: {
          resourceId: resource.id,
          resourceType: resource.type,
          requestAcknowledgement,
          conversationId,
        },
      })
    },
    [
      conversationId,
      handleNavigation,
      setSelectedConversationIds,
      setSelectedRecipientIds,
    ]
  )

  const onSelectMoveResource = useCallback((resource: Resource) => {
    setSelectedResource(resource)
    setSelectedAction('move')
  }, [])

  const navigateToFoldersScreen = useCallback(() => {
    if (conversationId && shouldNavigateToFoldersScreen) {
      handleNavigation({
        routeName: 'file_folders_bottom_tab',
        routeParams: {
          screen: 'files',
          params: {
            conversationId,
          },
          conversationId,
        },
      })
    }
  }, [conversationId, handleNavigation, shouldNavigateToFoldersScreen])

  const onSelectCopyResource = useCallback((resource: Resource) => {
    setSelectedResource(resource)
    setSelectedAction('copy')
  }, [])

  const openSortOrderBottomSheet = useCallback(() => {
    setBottomSheetType('options')
    setBottomSheetVisible(true)
  }, [])

  const onCloseBottomSheet = useCallback(() => {
    if (fileFolderBottomSheetRef.current) {
      fileFolderBottomSheetRef.current.close()
    }
    setBottomSheetVisible(false)
  }, [])

  const onPressThumbnail = useCallback(() => {
    if (longPressedResource?.type === 'file') {
      onCloseBottomSheet()
      handleNavigation({
        routeName: 'file_viewer',
        routeParams: {
          fileId: longPressedResource.id,
        },
      })
    } else if (longPressedResource?.type === 'folder') {
      if (!conversationId) {
        return
      }
      onCloseBottomSheet()
      handleNavigation({
        routeName: 'file_folders_bottom_tab',
        routeParams: {
          screen: 'files',
          params: {
            conversationId,
            path: longPressedResource.path?.concat({
              id: longPressedResource.id,
              name: longPressedResource.name,
            }),
          },
          conversationId,
        },
      })
    }
  }, [
    conversationId,
    handleNavigation,
    longPressedResource,
    onCloseBottomSheet,
  ])

  const onPressResourceAction = useCallback(
    async (resource: Resource, action: string) => {
      switch (action) {
        case 'share':
          onShareResource(resource, false)
          break
        case 'shareWithAck':
          onShareResource(resource, true)
          break
        case 'delete':
          const confirmation = await confirmationAlert(
            t('delete'),
            t('confirmDeleteItem')
          )
          if (confirmation) {
            onDeleteResource(resource).catch(e => errorAlert(e))
          }
          break
        case 'move':
          onSelectMoveResource(resource)
          navigateToFoldersScreen()
          break
        case 'copy':
          onSelectCopyResource(resource)
          navigateToFoldersScreen()
          break
        case 'edit':
          handleNavigation({
            routeName: 'edit_file_folder',
            routeParams: {
              id: resource.id,
              type: resource.type,
            },
          })
      }
      onCloseBottomSheet()
    },
    [
      handleNavigation,
      navigateToFoldersScreen,
      onCloseBottomSheet,
      onDeleteResource,
      onSelectCopyResource,
      onSelectMoveResource,
      onShareResource,
    ]
  )

  const onAddFolder = useCallback(
    async (fields: AddFolderParams) => {
      await addFolderMutation.mutateAsync({
        ...fields,
        chatGroupId: conversationId,
      })
    },
    [addFolderMutation, conversationId]
  )

  const onAddFile = useCallback(
    async (fields: AddFileParams) => {
      await addFileMutation.mutateAsync({
        ...fields,
        chatGroupId: conversationId,
      })
    },
    [addFileMutation, conversationId]
  )

  const openFileFolderDetails = useCallback((resource: Resource) => {
    setLongPressedResource(resource)
    setBottomSheetType('details')
    setBottomSheetVisible(true)
  }, [])

  const onLongPressResource = useCallback(
    (resource: Resource) => openFileFolderDetails(resource),
    [openFileFolderDetails]
  )

  const onPressFile = useCallback(
    (file: File) => {
      if (
        file.chatGroupId &&
        (isImageType(file.currentVersion?.fileMimeType) ||
          isVideoType(file.currentVersion?.fileMimeType))
      ) {
        handleNavigation({
          routeName: 'chat_file_viewer',
          routeParams: {
            fileId: file.id,
            conversationId: file.chatGroupId,
            currentFolderId: file.folderId,
          },
        })
        return
      }
      handleNavigation({
        routeName: 'file_viewer',
        routeParams: {
          fileId: file.id,
        },
      })
    },
    [handleNavigation]
  )

  const value: FileFolderActionsContextValue = {
    onAddFile,
    onAddFolder,
    onLongPressResource,
    onPressFile,
    setConversationId,
    onPerformSelectedAction,
    selectedAction,
    onCancelSelectedAction,
    setShouldNavigateToFoldersScreen,
    openSortOrderBottomSheet,
    sortOption,
    viewMode,
  }

  return (
    <FileFolderActionsContext.Provider value={value}>
      {props.children}
      {bottomSheetVisible && (
        <FileFolderBottomSheet
          onClose={onCloseBottomSheet}
          type={bottomSheetType}
          itemId={longPressedResource?.id}
          itemType={longPressedResource?.type}
          onPressAction={onPressResourceAction}
          onPressThumbnail={onPressThumbnail}
          onPressSortOption={setSortOption}
          onPressViewMode={setViewMode}
          selectedSortOption={sortOption}
          selectedViewMode={viewMode}
          ref={fileFolderBottomSheetRef}
        />
      )}
    </FileFolderActionsContext.Provider>
  )
}

export function useFileFolderActions() {
  return useContext<FileFolderActionsContextValue>(FileFolderActionsContext)
}
